import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Space, Form, Input, Select, Button, Checkbox, Typography, 
         Divider, Radio, Slider, Steps} from "antd";
// import axios from "axios";
import ReactMarkdown from 'react-markdown'

import MarkdownIt from 'markdown-it';

import { mclOpen } from "../../api/Mcl";
import { Url } from "../../constants/Global";

import { PasswordModal, ResubmitPlanModal, ResendPlanModal } from "../../components/PlanComponents"
import { use } from "react";


const { TextArea } = Input;
const { Title, Text } = Typography;

const initialPrompt = `
You are a Care Concierge and intelligent care planning assistant. A family caregiver has just submitted a questionnaire with information about their situation and the person in their care. Your task is to generate a complete, personalized, and empathetic care plan that reflects their input and anticipates needs across medical, emotional, logistical, legal, and financial domains.
Please generate an initial version of a Care Plan that is:
Accurate (based only on what was submitted — no assumptions)
Comprehensive (covers physical, emotional, logistical, financial, and legal needs)
Useful (organized, clear, task-oriented, jargon-free)
Empathetic (validates the caregiver's experience and emotional load)
Your output should include:

1. Situation Summary
Who is the family caregiver and who is the person receiving care?
What are the key health concerns, diagnoses, risks, and current supports?
Highlight what the caregiver is trying to manage, solve, or prevent.

2. Goals of Care
What are the short- and long-term priorities for the caregiver and the person in care?
What matters most to them emotionally, culturally, or functionally?
What do they hope to avoid (e.g. hospitalization, isolation, caregiver burnout)?

3. Identified Gaps & Missing Information
What important information is missing or underdeveloped?
List any potential gaps in care (e.g., no legal documents, lack of support, unmanaged symptoms).
Prompt follow-up questions to help move from 75% to 100% completeness.

4. 30/60/90 Day Task Plan
Structure short-term implementation clearly:
Task
Time to Complete
Value to Family
[e.g. Schedule PCP follow-up]
1 hour
Avoid missed care
[e.g. Organize legal documents]
3 hours
Ensures decision-making clarity
[e.g. Set up shared calendar]
1 hour
Improves coordination


5. 12–18 Month Planning
Sequence the following long-term planning categories:
Legal documents (POA, Advance Directive, POLST)
Financial planning and cost projections
Housing transitions
End-of-life preferences
Funeral/final arrangements
After-life caregiving and closure for family

6. Emotional Support & Validation
Acknowledge the caregiver’s effort, stress, and complexity of their role.
Validate that their concerns are real, common, and manageable with the right support.
Offer a message of encouragement and support.

7. Suggested Next Steps
Clearly lay out 2–3 next actions for the family to take in the next 7–14 days.
Identify any urgent concerns or tasks that should not be delayed.

8. CTA: Upgrade to Concierge Support
Need help making this plan happen?
Upgrade to MyCareLinq Plus to get:
A dedicated Care Concierge
Help scheduling and managing services
Monthly progress reviews and family updates
Peace of mind knowing you’re not doing this alone
Upgrade now to simplify caregiving and reduce your stress.


Then, after you have generated the initial version care plan detailed above, use this initial version of the care plan to generate 
but not included in the final version of the care plan using the following instructions and output in raw Markdown only:  

You are a care plan formatting expert with deep emotional intelligence and a strong grasp of caregiver
 stress, decision fatigue, and systemic complexity.
 Your job is to take a **generated family care plan** and present it in a clear, emotionally supportive,
 and professional format that reflects the values and brand tone of MyCareLinq.  
 Your formatting must achieve the following:- Be **accurate**: reflect the caregiver’s actual context and care needs- Be **comprehensive**: include medical, emotional, logistical, financial, and legal needs- Be **useful**: easy to scan, act on, and delegate- Be **empathetic**: human, validating, and emotionally attuned- Be **professional**: no icons, no emojis, no casual tone — warm, polished, and executive-ready
 Do not rewrite the care plan content. Your job is to format and *emotionally shape* it to maximize
 clarity, trust, and activation.--
 Structure and Instructions
 1. Header
 Present basic details clearly.- Title: *Family Care Plan*- Generated for: [Client First Name Last Initial]- Date: [Insert Date]- Prepared by: MyCareLinq Care Team
 2. Situation Summary
Narrate the caregiver’s current situation with warmth and reflection. Use empathetic language that:- Acknowledges complexity and effort- Names stressors and caregiving realities clearly- Avoids medical or bureaucratic jargon
 3. Goals of Care
 Summarize what the caregiver hopes to achieve — short- and long-term. Format as:- Immediate (0–30 days)- Short-Term (1–3 months)- Long-Term (3–12+ months)
 4. Key Gaps and Challenges
 Help the user understand where support is most needed — without overwhelming them.
 Use this table structure:
 | Category           | Challenge                                           | Urgency   |
 |--------------------|-----------------------------------------------------|-----------|
 5. 30 / 60 / 90 Day Action Plan
 Frame this section as “here’s a manageable path forward.” Use a table:
 | Timeframe | Action                                   | Owner         | Status     |
 6. 12–18 Month Planning Outlook
 Offer a horizon view to help the user feel one step ahead — not left behind.
 Use bullets or short paragraphs. Focus on:- Housing transitions- Financial sustainability- Long-term care preferences
- Contingency plans for caregiver capacity
 7. Emotional Validation & Mental Health Support
 This is your human moment. Reflect what the caregiver expressed emotionally.- Mirror emotional tone (grief, guilt, fatigue, fear, devotion)- Acknowledge how much they’ve carried- Validate that it’s okay to need help- Include at least one suggested support action if caregiver mentioned stress, illness, lost sleep, or
 deferred appointments
 8. Suggested Next Steps
 Offer 3–5 clear, non-intimidating next steps. Use a checklist or bullets. Highlight which ones can be
 delegated.
 9. What MyCareLinq Can Handle — Support Matrix
 Show the caregiver exactly what MCL can take off their plate — and how much time it might save.
 Use this table structure:
 | Timeframe   | Task                                  | Value to Family                      | Est. Time
 to Complete | Handled by MCL? |
 |-------------|----------------------------------------|---------------------------------------|------------------------|-----------------|
 10. Call to Action — Care Concierge Session
 End with an invitation to schedule a 1:1 planning session. Use calm, validating language and a direct
 CTA.
 Example:
 > You don’t have to carry all this alone.
 > Schedule a time to speak with your MyCareLinq Care Concierge — we’ll help you prioritize, delegate, and
 move forward.


`;

// > [Schedule your session here.](https://calendly.com/mycarelinq)

const labelMap = {
    caregiverLocation: {n: "caregiverLocation", q: "Where are you currently located?", p: "City, State"},
    recipientLocation: {n: "recipientLocation", q: " Where is the person you're caring for located?", p: "City, State"},
    careDuration: {n: "careDuration", q: "How long have you been providing care?", p: "e.g. 6 months, 2 years"},
    weeklyHours: {n: "weeklyHours", q: "On average, how many hours per week do you spend caregiving?"},
    tasks: {n: "tasks", q: "Which caregiving tasks do you handle regularly?", 
      p: "transportation, meal preparation, bathing/dressing, medications, finances, companionship, medical decision making"},
    helpers: {n: "helpers", q: "Who else supports you in your caregiving role?", 
      p: "no one, family, paid professional, friends, community/faith-based support"},
    distress: {n: "distress", q: "What’s felt especially hard about caregiving lately?", 
      p: "This can be emotional, logistical, or anything else you'd like to share."},
    feeling: {n: "feeling", q: "What challenges or feelings have you been experiencing recently?", 
      p: "Feeling emotionally drained, Confused about what’s expected of you,Unsafe or emotionally harmed,Delaying your own needs,Feeling overwhelmed or close to burnout (Scale: Never → Always) "},
    confidence: {n: "confidence", q: " Which caregiving areas do you feel least confident or clear about?" },
    workImpact: {n: "workImpact", q: "Has caregiving affected your work or income in any way?", 
      p: "yes or no, please share more"},
    // expenseResponse: {n: "expenseResponse", q: "If a $1,000 care-related expense came up today, how would you manage it?", 
    //   p: "savings, credit, delay, ask family, unsure, please elaborate"},
    supportTools: {n: "supportTools", q: "Have you already tried any tools, services, or providers that didn't meet your expectations?"}, 
    supportTypes: {n: "supportTypes", q: "Which types of support would you consider accepting if offered?", 
      p: "emotional support, legal/financial help, care planning, respite care, finding housing, paid caregiver support,  please elaborate"},
    personalInsight: {n: "personalInsight", q: "(Optional) What’s something you wish others better understood about your caregiving situation?"},
    personalFuture: {n: "personalFuture", q: "(Optional) What worries you most about the future of this care situation?"},
    personalSituation: {n: "personalSituation", q: "(Optional) If you could reshape your caregiving situation in any way, what would that look like?"},
  }

const mclInitialValues = {
        [labelMap.caregiverLocation.n]: "San Francisco, California",
        [labelMap.recipientLocation.n]: "Sacramento, CA",
        [labelMap.careDuration.n]: "18 months",
        [labelMap.weeklyHours.n]: "15",
        [labelMap.tasks.n]: "I've had to manage more and more as I find things. I'm providing rides because it's generally unsafe for my mom to drive anymore. I'm running errands for her and now need to discuss how to manage her finances and be her POA for medical decisions.",
        [labelMap.helpers.n]: "my sister but she is busy and doesn't have a lot of time",
        [labelMap.distress.n]: "I really don't want to be doing this but there isn't any alternative. I feel like this is being forced on to me. My mother plays the victim. She is moody and doesn't express any appreciation for how much time and resources its taking me to care for her. I have no idea where or who to talk to about how to apply for POA. I don't have an understanding of how she is doing financially. I don't know where were records are or where she keeps her passwords. I'm really frustrated and I feel like I can't talk about it because I sound angry all the time. And no one understands.",
        [labelMap.feeling.n]: "I'm so tired and stressed about how much work this is. I have a feeling its only going to get worse. I really don't know what to do to help my mom. I really don't want to do this on my own. I've also lost sleep, and get migraines, and emotionally I'm all over the place.",
        [labelMap.confidence.n]: "Her estate stuff, POA, Advance Directive, insurance and what her wishes are.",
        [labelMap.workImpact.n]: "I've taken time away from work to figure out what is going on in her home. Every time I visit I find something new. I'm spending my money on small things like drug store items, groceries, and transportation.",
        [labelMap.supportTools.n]: "Not yet. I'm still trying to figure where things are.",
        [labelMap.supportTypes.n]: "I really don't have the time or capacity to take this on. I'm not an expert and the time it takes to research each new thing totally distracts me from work, family, and my own needs.",
        [labelMap.personalInsight.n]: "It's really not fair to put something in this type of position. I feel like this situation is totally out of control and I have to make it better somehow.",
        [labelMap.personalFuture.n]: "If she has an accident that puts her in the hospital, I'm worried that I'll have to be her main support and I don't want that. I'm also worried that she will outlive her money.",
        [labelMap.personalSituation.n]: "Honestly, I wish I didn't have to be the one doing all of this for my mom. I'm so stressed and upset that is taking all my time and focus.",
}


export const  WanidoCarePlan = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paramEmail = queryParams.get('email');

  var initVal = {} 
  const emailDomain = paramEmail.split('@')[1];
  if (emailDomain === "mycarelinq.com") {
    initVal = mclInitialValues;
  }

  const [qa, setQA] = useState("");
  const [prompt, setPrompt] = useState(initialPrompt);
  const [description, setDescription] = useState("mcl");
  const [plan, setPlan] = useState("");
  const [loading, setLoading] = useState(false);
  const [mailing, setMailing] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [authkey, setAuthkey] = useState(null);
  const [changekey, setChangekey] = useState(null);
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState(paramEmail);
  const [org, setOrg] = useState("Wanido")
  const [planHtml, setPlanHtml] = useState("<></>");
  const [status, setStatus] = useState("")
  const [initialValues, setInitialValues] = useState(initVal)
  const [showResubmit, setShowResubmit] = useState(false)
  const [showResend, setShowResend] = useState(false)

  const md = new MarkdownIt();


  const validatePassword = () => {
    setAuthkey(changekey);
  }

  const handleResubmit = () => {
    setShowResubmit(false);
  }

  const handleResend = () => {
    setShowResend(false);
  }

  const handleProcess = async (values) => {
    console.log(values)
    setLoading(true);
    const currentQA = "Questionnaire with Answers:\n\n" + Object.entries(values)
      .map(([key, value]) => `Question: ${labelMap[key].q} ${labelMap[key]?.p ? "("+labelMap[key]?.p+")" : ""} \nAnswer: ${value ? value: 'No answer'} \n`)
      .join('\n')

    setQA(currentQA)
    setPlan("");
    // console.log(Url.PLAN.concat("/ai-wb"));
    console.log(Url.PLAN.concat("/ai"));
    try {      
      // const response = await mclOpen.post(Url.PLAN.concat("/ai-wb"),
      const response = await mclOpen.post(Url.PLAN.concat("/ai"),
        JSON.stringify({
          email,
          org,
          qa: currentQA,
          prompt,
          description,
        }),
        {
          headers: { "Content-Type": "application/json" },
        }
      )
      
      console.log("Response:", response?.data);
      setPlan(response?.data || "No result returned.");
      setPlanHtml(md.render(response?.data ?? ""));
      setStep(3)
    } catch (err) {
      console.log(`Error processing request error: ${err.message}`);
      setPlanHtml(`Error processing request error: ${err.message}`);
      setShowResubmit(true)
    }
    setLoading(false);
  };

  const handleEmail = async () => {
    console.log("Sending email")
    try {      
      setMailing(true);
      const response = await mclOpen.post(Url.PLAN.concat("/ai-send"),
        JSON.stringify({
          email,
          org,
          qa,
          plan,
        }),
        {
          headers: { "Content-Type": "application/json" },
        }
      )

      console.log("sending successful!")
      setMailing(false)
      // setStep(4)
      window.location.href = 'https://wanido.mycarelinq.com/application-thank-you?preview=true';
      //console.log("Response:", response?.data);
      //setPlan(response?.data || "No result returned.");
      //setStep(4)
    } catch (err) {
      console.log("sending failed!!")
      setMailing(false)
      setShowResend(true)
    }
  }

  useEffect(() => {
    // const compareHash = async (str) => {
    //   const encoder = new TextEncoder();
    //   const data = encoder.encode(str);
    //   const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    //   const hashArray = Array.from(new Uint8Array(hashBuffer));
    //   const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
    //   var isCheck = false;
    //   // console.log(str, hashHex);
    //   // if (hashHex === "5d601c9f3f0339f619b6690cf01635231a8434f780e1e430096b91b4e3df5aab") {
    //   //   // default mcl
    //   //   setIsAuthorized(true);
    //   // } 
    //   // aon  f46214271123719994ad0331840c3510bace3f9b0ee4dd557ea0dd34823dd41e
    //   // silver f14a6134f7629a194b05c6d3b57c402f9160d7e3534d6693efcf842d1eba7fa4
    //   // return hashHex;
    //   switch (hashHex) {
    //     case "5d601c9f3f0339f619b6690cf01635231a8434f780e1e430096b91b4e3df5aab":
    //       setDescription("mcl");
    //       setOrg("MyCareLinq")
    //       setIsAuthorized(true);
    //       setInitialValues(mclInitialValues)

    //       // isCheck = true          
    //       break;
    //     case "f14a6134f7629a194b05c6d3b57c402f9160d7e3534d6693efcf842d1eba7fa4":
    //       setDescription("silver");
    //       setOrg("Silver Lake")
    //       setIsAuthorized(true);          
    //       break;
    //     case "bf863ec0c255ad5c32379bf7cf4366e1e177f8f2aafe39757608ba8aa3880080":
    //       setDescription("guest");
    //       setOrg("Guests")
    //       setIsAuthorized(true);          
    //       break;

    //     case "f46214271123719994ad0331840c3510bace3f9b0ee4dd557ea0dd34823dd41e":
    //       setDescription("aon");
    //       setOrg("Aon")
    //       // setIsAuthorized(true);          
    //       isCheck = true          
    //       break;
    //     case "c280284088cce74ff48b93b940286001ab240f3bea8d2fd8214d908823652e7b":
    //       setDescription("aon");
    //       setOrg("Aon")
    //       // setIsAuthorized(true);
    //       isCheck = true          
    //       break;
    //     case "f04bf33843736a997797bbcb4186c9fa0ca6314ba6804569b8c7beaf92e5abcd":
    //       setDescription("aon");
    //       setOrg("Aon")
    //       // setIsAuthorized(true);
    //       isCheck = true          
    //       break;
    //     case "31eecbe97a67437989fe956be6c966e7598530a2c13fdd81e36403b1c4f7b390":
    //       setDescription("aon");
    //       setOrg("Aon")
    //       // setIsAuthorized(true);
    //       break;

    //     default:
    //       setDescription("none");
    //   }

    //   if (isCheck) {
    //     try {      
    //       // set(true);
    //       const response = 
    //         await mclOpen.get(Url.PLAN.concat("/access/", hashHex))
    //           .then((res) => {
    //             console.log("response", res.data);
    //             setIsAuthorized(true)
    //           })
    //           .catch((error) => {
    //             const msg = "Access limit exceeded";
    //             setStatus(msg);
    //             console.log(msg);
    //           })

    //     } catch (err) {
    //       console.log("Access check failed!!")
    //     }      
    //   }
    // };

    // if (!isAuthorized) {
    //   compareHash(changekey);
    // }
  
  // }, [authkey])
    // setOrg("Wanido")
    // form.setFieldsValue({ org: "Wanido" }); // ✅ Dynamically update field
  }, [location.search])

  
const { Title, Paragraph } = Typography;
const { Option } = Select;
const { Step } = Steps
const { TextArea } = Input

const [form] = Form.useForm();

const handleEmailOrgSubmit = (values) => {
  setEmail(values.email)
  setOrg(values.org)
  setStep(1)
}

const handleBegin = () => {
  setStep(2)
}


const ruleRequired = [
    { required: true, message: "Please fill out this field." }
  ]
  
  


  // return !isAuthorized ? <PasswordModal handleOk={validatePassword} modalShow={!isAuthorized} onKey={(val) => {setChangekey(val)}} status={status}/> : 
  return (
    <div style={{ width: '90%', margin: "auto", padding: 24 }}>
      <ResubmitPlanModal handleOk={handleResubmit} modalShow={showResubmit}/>
      <ResendPlanModal handleOk={handleResend} modalShow={showResend} email={email}/>
      <Steps current={step} style={{ marginBottom: 24 }}>
        <Step title="Welcome" />
        <Step title="Before You Begin" />
        <Step title="Questionnaire" />
        <Step title="Family Care Plan" />
        <Step title="Thank You" />
      </Steps>

      {step === 0 && (
        <Form layout="vertical" onFinish={handleEmailOrgSubmit}  initialValues={{ email: email, org: org }}>
          <Typography style={{ marginBottom: 24 }}>
            <Title level={3}>Welcome to My CareLinq</Title>
            <Paragraph>You’re in the right place.</Paragraph>
            <Paragraph>
              This experience is part of a family caregiving benefit provided by your employer, health plan, or membership organization — in partnership with My CareLinq.
              We’ll help you create a personalized family caregiving plan, with expert support, curated services, and actionable next steps to help you and your family navigate care with confidence.
            </Paragraph>
            <Title level={4}>What to Expect</Title>
            <ul>
              <li>You’ll answer a few questions about yourself and your caregiving role</li>
              <li>It takes about 10–20 minutes</li>
              <li>You’ll receive a customized care plan that can be saved, updated, and shared</li>
            </ul>
            <Title level={4}>Verify Your Affiliation</Title>
          </Typography>
          <Form.Item name="org" label="Organization" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit">Proceed to Enrollment</Button>
        </Form>
      )}

      {step === 1 && (
          <div style={{ maxWidth: 700, margin: 'auto', padding: '2rem' }}>
          <Typography>
            <Title level={2}>Enterprise Enrollment Page</Title>
            <Title level={3}>Welcome to Your Caregiving Benefit</Title>
            <Paragraph>
              This caregiving benefit is brought to you by <strong>[Organization Name]</strong> and My CareLinq.
            </Paragraph>

            <Title level={4}>What to Expect</Title>
            <ul>
              <li>Answer a few brief questions about your caregiving situation</li>
              <li>Estimated time: 10–20 minutes</li>
              <li>You’ll receive a personalized family care plan you can download, email, or refine later</li>
            </ul>

            <Title level={4}>Your Benefit Package Includes</Title>
            <ul>
              <li>Self-driven family care plan generation — complete a guided assessment and receive a customized plan you can update over time</li>
              <li>Schedule time with a Care Concierge — review your plan, explore solution options, and upgrade to full-service support if needed</li>
            </ul>

            <Title level={4}>Ready to Build Your Family Care Plan?</Title>

            <Title level={5}>Before You Begin</Title>
            <ul>
              <li>You’ll answer questions about your caregiving role, your loved one’s health, and any support systems you may already have</li>
              <li>This will take about 10–20 minutes</li>
              <li>You can pause, save, and return at any time</li>
            </ul>
            <Paragraph>
              <strong>Why we ask:</strong> This helps us tailor your care plan and recommendations — and prepares our team to support you if you want more help.
            </Paragraph>

            <Title level={5}>Tips Before You Start</Title>
            <ul>
              <li>Be honest — your information is private and secure</li>
              <li>Take your time — revisit questions if you need</li>
              <li>We’re here for you — your responses inform the best path forward</li>
            </ul>

            <div style={{ margin: '2rem 0' }}>
              <Button type="primary" size="large" onClick={handleBegin}>
                Begin My Family Care Plan
              </Button>
            </div>

          </Typography>
        </div>
      )}

      {step === 2 && (
        <Form layout="vertical" form={form} onFinish={handleProcess}
          initialValues={initialValues}
        >
        <h3><bold>Let’s start with a few quick questions to get oriented—then you’ll have a chance to share more about your situation if you’d like.</bold></h3>
        <Divider orientation="left" orientationMargin={0}>Core Questions</Divider>
          <Form.Item label={labelMap.caregiverLocation.q} >
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '12px'  }}>
              {labelMap.caregiverLocation.p}
            </Text>
            <Form.Item name={labelMap.caregiverLocation.n} rules={ruleRequired}>
              <Input/>
            </Form.Item>
          </Form.Item>

          <Form.Item label={labelMap.recipientLocation.q} >
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '12px'  }}>
              {labelMap.recipientLocation.p}
            </Text>
            <Form.Item name={labelMap.recipientLocation.n} rules={ruleRequired}>
              <Input/>
            </Form.Item>
          </Form.Item>

          <Form.Item label={labelMap.careDuration.q} >
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '12px'  }}>
              {labelMap.careDuration.p}
            </Text>
            <Form.Item name={labelMap.careDuration.n} rules={ruleRequired}>
              <Input/>
            </Form.Item>
          </Form.Item>

          <Form.Item label={labelMap.weeklyHours.q} >
          <Form.Item name={labelMap.weeklyHours.n} rules={ruleRequired}>
            <Input />
          </Form.Item>
          </Form.Item>

          <Form.Item label={labelMap.tasks.q} >
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '12px'  }}>
              {labelMap.tasks.p}
            </Text>
            <Form.Item name={labelMap.tasks.n} rules={ruleRequired}>
              <Input.TextArea rows={4} />
            </Form.Item>
          </Form.Item>
 
          <Form.Item label={labelMap.helpers.q} >
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '12px'  }}>
              {labelMap.helpers.p}
            </Text>
          <Form.Item name={labelMap.helpers.n} rules={ruleRequired}>
            <Input.TextArea rows={2}/>
         </Form.Item>
         </Form.Item>

          <Divider orientation="left" orientationMargin={0}>Distress & Burnout</Divider>

          <Form.Item label={labelMap.distress.q} >
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '12px'  }}>
              {labelMap.distress.p}
            </Text>
            <Form.Item name={labelMap.distress.n} rules={ruleRequired}>
              <Input.TextArea rows={4}/>
            </Form.Item>
          </Form.Item>

          <Form.Item label={labelMap.feeling.q} >
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '12px'  }}>
              <ul>
                <li>Feeling emotionally drained</li>
                <li>Confused about what’s expected of you</li>
                <li>Unsafe or emotionally harmed</li>
                <li>Delaying your own needs</li>
                <li>Feeling overwhelmed or close to burnout</li>
              </ul>           
              <p>(Scale: Never → Always)</p>
            </Text>
            <Form.Item name={labelMap.feeling.n} rules={ruleRequired}>
              <Input.TextArea />
            </Form.Item>
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>Confidence Mapping</Divider>

          <Form.Item label={labelMap.confidence.q} >
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '12px'  }}>
              {labelMap.confidence.p}
            </Text>
            <Form.Item name={labelMap.confidence.n} rules={ruleRequired}>
              <Input.TextArea rows={2} 
                placeholder=""
              />
            </Form.Item>
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>Financial & Work Impact</Divider>

          <Form.Item label={labelMap.workImpact.q}>
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '12px'  }}>
              {labelMap.workImpact.p}
            </Text>
            <Form.Item name={labelMap.workImpact.n} noStyle>
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>Support Needs</Divider>
          <Form.Item label={labelMap.supportTools.q}>
            {/* <Text type="secondary" style={{ fontSize: '12px', marginLeft: '12px'  }}>
              {labelMap.supportTools.p}
            </Text> */}
            <Form.Item name={labelMap.supportTools.n}>
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form.Item>

          <Form.Item label={labelMap.supportTypes.q}>
          <Text type="secondary" style={{ fontSize: '12px', marginLeft: '12px'  }}>
              {labelMap.supportTypes.p}
            </Text>
            <Form.Item name={labelMap.supportTypes.n}>
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>Optional Personal Insight</Divider>

          <Form.Item name={labelMap.personalInsight.n} label={labelMap.personalInsight.q}>
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item name={labelMap.personalFuture.n} label={labelMap.personalFuture.q}>
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item name={labelMap.personalSituation.n} label={labelMap.personalSituation.q}>
            <Input.TextArea rows={4} />
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>Review & Next Steps</Divider>

          <Divider orientation="left" orientationMargin={0}>Before You Submit</Divider>
            <Typography>
            <Paragraph>
              Take a moment to review your answers. You can go back to any section and make changes.
              You do not need to answer every question to receive support—just do your best.
            </Paragraph>

            <Divider orientation="left" orientationMargin={0}>What Happens Next</Divider>
            <Paragraph>
              After you submit, our Care Concierge system will generate a personalized Family Care Plan based on your responses.
              Within a few minutes, you’ll receive your plan — including a summary of your caregiving situation, practical next steps tailored to your responses, and relevant suggestions and resources to help you move forward.
            </Paragraph>
            <Paragraph>
              You may also receive an invitation to connect with a Care Concierge for one-on-one help with implementing your plan and coordinating next steps (optional).
            </Paragraph>

            <Paragraph>
              Thank you for sharing your story. Your responses help us better support you and your loved one in the journey ahead.
            </Paragraph>

            <div style={{ margin: '2rem 0' }}>
              <Form.Item>
                <Button type="primary" size="medium" htmlType="submit"  loading={loading}>
                  Submit Responses and Generate My Care Plan
                </Button>
              </Form.Item>
            </div>

            <Paragraph type="secondary" style={{ fontSize: '0.9rem' }}>
              By clicking submit, your responses will be securely sent to our Care Concierge system. Within a few minutes, you’ll receive your personalized care plan via email or secure link.
            </Paragraph>
            <Paragraph type="secondary" style={{ fontSize: '0.9rem' }}>
              Need help? Contact us at <a href="mailto:support@mycarelinq.com">support@mycarelinq.com</a> or &nbsp;
              <a href="https://calendly.com/mycarelinq">Schedule a Concierge Session</a>
            </Paragraph>
          </Typography>


        </Form>
      )}

      {step === 3 && (
        <div style={{ width: '80%', margin: 'auto', padding: '2rem' }}>
        <Title level={3}>Family Care Plan</Title>

          <Typography>
            {/* <ReactMarkdown>{plan}</ReactMarkdown> */}
            <div dangerouslySetInnerHTML={{ __html: planHtml }} />
            </Typography>

          <Button type="primary" size="large" onClick={handleEmail} loading={mailing}>
            Email Family Care Plan
          </Button>
        </div>
      )}

      {step === 4 && (
        <div className="max-w-2xl mx-auto p-8 text-center" role="main" aria-label="Thank You Page">
          <h1 className="text-3xl font-bold mb-4">Your Family Care Plan Is On Its Way</h1>

          <p className="text-lg mb-4">
            <strong>Thank You for Trusting MyCareLinq</strong>
          </p>

          <p className="text-md mb-4">
            Your personalized family caregiving plan has been generated and is on its way to your inbox.
            You can review it, return anytime to update, or share it with someone you trust.
          </p>

          <p className="text-md mb-4">
            If you requested a follow-up from a Care Concierge, we’ll be reaching out shortly.
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded mb-4">
            <p className="text-sm text-blue-700">
              Check your email for a copy of your plan. If it’s not there within a few minutes, please check your spam folder or contact us at <strong>support@mycarelinq.com</strong>.
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-300 p-4 rounded mb-4">
            <p className="text-sm text-gray-800">
              You may now <strong>close this tab</strong> or return to your organization’s benefits portal.<br />
              To update your plan later, visit <strong>www.mycarelinq.com</strong> and log in with your email.<br />
              Need help or prefer to talk to someone? Call us at <strong>1-408-214-2416</strong>.
            </p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => window.location.href = '/'}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
            >
              Return to Homepage
            </button>
            <button
              onClick={() => {
                 window.open("https://calendly.com/mycarelinq", "_blank");                }
              }
              className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            >
              Schedule a Concierge Follow-Up
            </button>
          </div>
        </div>      
      )}
    </div>
  );

}
